import { forwardRef, useState } from "react";
import Tooltip from "../../tooltip/Tooltip";
import "./_text-input.scss";

/**
 * Pure-UI text input. onChange receives the raw value (not the DOM event) -
 * matches every other input convention in this codebase so it's a smaller
 * diff to swap in at existing call sites. No validation logic lives here;
 * that's the caller's job (e.g. react-hook-form).
 *
 * `tooltip`: composes the existing `_components/tooltip/Tooltip` around the
 * field rather than reimplementing hover/positioning logic - pass a string
 * (typically paired with `disabled`, to explain why a field can't be edited).
 *
 * `locked` + `badge` (both optional, backward-compatible): for a field that
 * isn't just temporarily read-only but permanently system-managed (Platform
 * Settings' "Wallet on" / "Invoice on" - always follow the subscription
 * cycle, no user ever edits them) - `locked` swaps the field-wrapper's fill
 * for a diagonal hatch pattern with a dashed border (design's own
 * companies-workspace.dc.html "SYSTEM" field chrome) so it reads as
 * *structurally* uneditable, not just disabled-for-now like every other
 * field is while its section isn't in edit mode. `badge` renders a small
 * "SYSTEM"-style pill next to the label - pass both together; `locked`
 * without a `disabled` value forwarded alongside it won't itself block
 * typing, the caller still owns that.
 *
 * `helper` (optional): a caption line between the label and the field
 * itself, explaining what the value does (design's own "Shown before every
 * invoice number." line under "Invoice prefix") - distinct from `tooltip`,
 * which only surfaces on hover and is reserved for *why a field can't be
 * edited right now*, not what it's for.
 *
 * `unit` (optional): a muted suffix inside the field wrapper, right of the
 * value (design's "10 [days]" - a number field paired with its unit rather
 * than folding the unit into the label).
 *
 * `preview` (optional): a small monospace accent chip in the label row,
 * before the label text (design's "INV-001  Invoice prefix" - a live
 * preview of a prefix+start-number pair, e.g. Platform Settings' invoice/
 * proposal/customer prefixes). Distinct from `badge`: `preview` is accent-
 * colored and shows a *computed value*, `badge` is a neutral-gray *status*
 * label like "SYSTEM".
 *
 * Liquid Glass pass: the label always renders on its own line above the
 * field now (matching `TextArea`/`Checkbox`, and the design's own "Company
 * name" / "Acme Inc." field layout) - previously it doubled as the
 * placeholder until the field had a value, then jumped to a caption line
 * above it. That empty-vs-filled height mismatch is also why every field
 * used to need an explicit fixed height; every field is the same 2-line
 * shape unconditionally now, so that's gone too. `compact` (table cells)
 * is unaffected - it still skips the label entirely.
 */
const TextInput = forwardRef(function TextInput(
  {
    id,
    label,
    value,
    onChange,
    onBlur,
    error,
    disabled = false,
    required = false,
    type = "text",
    placeholder = "",
    tooltip,
    compact = false,
    locked = false,
    badge,
    preview,
    helper,
    unit,
    className = "",
    ...rest
  },
  ref
) {
  // `compact` (table cells, where a column header already labels the field)
  // skips the label slot entirely - see _text-input.scss's &--compact.
  const showLabel = Boolean(label) && !compact;
  // D4 - `required` was only ever forwarded to the native `<input
  // required>` attribute (an HTML5 constraint the caller's own submit flow
  // doesn't rely on - see buildResolver.js), so a required field gave no
  // visual signal at all until the user hit its "X is required" error on
  // submit ("Company short name" on Create Company was the field this
  // surfaced on, but the gap is generic to every required TextInput).
  const displayLabel = required && label ? `${label} *` : label;

  // Secret fields (type="password" - integration API secrets, DB passwords)
  // get a show/hide eye toggle so a pasted secret can be checked before
  // saving. Local state only; the actual input type is swapped, nothing
  // about the value or onChange changes.
  const isPasswordField = type === "password";
  const [isRevealed, setIsRevealed] = useState(false);

  return (
    <Tooltip content={tooltip} disabled={!tooltip} className="text-input-tooltip-anchor">
      <div
        className={`text-input ${error ? "text-input--error" : ""} ${
          locked ? "text-input--locked" : disabled ? "text-input--disabled" : ""
        } ${compact ? "text-input--compact" : ""} ${className}`}
      >
        {showLabel && (
          <div className={`text-input__label-row ${preview ? "text-input__label-row--with-preview" : ""}`}>
            <label className="text-input__label" htmlFor={id}>
              {displayLabel}
            </label>
            {preview && <span className="text-input__preview-chip">{preview}</span>}
            {badge && <span className="text-input__badge">{badge}</span>}
          </div>
        )}
        {showLabel && helper && <span className="text-input__helper">{helper}</span>}
        <div className="text-input__field-wrapper">
          <input
            ref={ref}
            id={id}
            type={isPasswordField && isRevealed ? "text" : type}
            className="text-input__field"
            value={value ?? ""}
            // `compact` never renders a label element (see showLabel above)
            // - falls back to the label as its placeholder so the field
            // still hints what it is, same as before this pass (asterisk
            // included, so a required compact field still signals it).
            // Non-compact fields show their label as a real line above
            // now, so the placeholder stays exactly what's passed (no more
            // doubling).
            placeholder={compact ? placeholder || displayLabel : placeholder}
            // Deliberately readOnly, not the native `disabled` attribute:
            // browsers suppress mouse events on disabled controls almost
            // entirely, which silently breaks the composed Tooltip above
            // (its hover handlers never fire). readOnly blocks editing
            // while staying fully hoverable/focusable/selectable - a better
            // fit for "can't edit this, here's why" fields anyway.
            readOnly={disabled}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : undefined}
            required={required}
            autoComplete="off"
            onChange={(e) => onChange?.(e.target.value)}
            onBlur={onBlur}
            {...rest}
          />
          {unit && <span className="text-input__unit">{unit}</span>}
          {isPasswordField && (
            <button
              type="button"
              className="text-input__reveal"
              onClick={() => setIsRevealed((prev) => !prev)}
              aria-label={isRevealed ? "Hide value" : "Show value"}
              aria-pressed={isRevealed}
              title={isRevealed ? "Hide" : "Show"}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {isRevealed ? (
                  <>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </>
                ) : (
                  <>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </>
                )}
              </svg>
            </button>
          )}
        </div>
        {error && <p className="text-input__error">{error}</p>}
      </div>
    </Tooltip>
  );
});

export default TextInput;
